'use client';

import { useEffect, useId, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';

import type { HeaderData } from './header-data';

import Link from '@eliadmin/core/src/components/link';
import { CmsIcon } from '@eliadmin/core/src/components/svetech/ui/cms-image';
import { Icon } from '@eliadmin/core/src/components/svetech/ui/icon';
import { cn } from '@eliadmin/core/src/utils/style-utils';

import { MegaMenuPanel } from './mega-menu';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

const OPEN_DELAY = 80;
const CLOSE_DELAY = 150;

/** Second header bar with the category groups and their mega menus (desktop). */
export function HeaderNav({
  groups,
  benefits,
  advice,
}: Pick<HeaderData, 'groups' | 'benefits' | 'advice'>) {
  const [openId, setOpenId] = useState<number | null>(null);
  const { t } = useTranslations();
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const triggers = useRef(new Map<number, HTMLButtonElement>());
  const root = useRef<HTMLDivElement>(null);
  const pathname = usePathname();
  const panelId = useId();

  const schedule = (next: number | null, delay: number) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setOpenId(next), delay);
  };

  useEffect(() => setOpenId(null), [pathname]);
  useEffect(() => () => clearTimeout(timer.current), []);

  useEffect(() => {
    if (openId === null) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      triggers.current.get(openId)?.focus();
      setOpenId(null);
    };
    // Touch and click outside the bar and the panel (tablets have no hover to leave).
    const onPointerDown = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpenId(null);
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [openId]);

  const openGroup = groups.find(group => group.id === openId);

  return (
    <div
      ref={root}
      onMouseLeave={() => schedule(null, CLOSE_DELAY)}
      onBlur={event => {
        // Keyboard focus moved outside the bar and the panel (pointer clicks are handled above).
        const next = event.relatedTarget as Node | null;
        if (next && !event.currentTarget.contains(next)) setOpenId(null);
      }}
    >
      <div className="custom-section py-2">
        <nav
          aria-label={t('text176')}
          className="custom-container flex items-center justify-between gap-8"
        >
          <ul className="flex items-center gap-8">
            {groups.map(group => {
              const open = group.id === openId;
              return (
                <li key={group.id} className="relative">
                  <button
                    ref={node => {
                      if (node) triggers.current.set(group.id, node);
                      else triggers.current.delete(group.id);
                    }}
                    type="button"
                    aria-expanded={open}
                    aria-controls={open ? panelId : undefined}
                    onClick={() => {
                      clearTimeout(timer.current);
                      setOpenId(open ? null : group.id);
                    }}
                    onMouseEnter={() => schedule(group.id, openId === null ? OPEN_DELAY : 0)}
                    className={cn(
                      'svt-button flex items-center gap-2 font-bold text-[#14171a] transition-colors hover:text-[#2c7a25]',
                      // Figma „Active indicator“: 3 px green bar on the bottom edge of the bar.
                      open &&
                        'text-[#2c7a25] after:absolute after:inset-x-0 after:-bottom-2 after:h-[3px] after:bg-[#2c7a25]',
                    )}
                  >
                    <CmsIcon image={group.icon} className="size-6" />
                    {group.name}
                    <Icon name="caret-down" className="size-4 text-[#6b7681]" />
                  </button>
                </li>
              );
            })}
          </ul>
          {advice ? (
            <Link
              href={advice.href}
              prefetch={false}
              className="svt-button flex items-center gap-2 font-bold text-[#2c7a25] hover:text-[#123410]"
            >
              <Icon name="question" className="size-6" />
              {advice.label}
            </Link>
          ) : null}
        </nav>
      </div>
      {openGroup ? (
        // Figma „Overlay“: #14171a at 45 % over the page under the open menu.
        <div
          aria-hidden="true"
          onMouseEnter={() => schedule(null, CLOSE_DELAY)}
          onClick={() => setOpenId(null)}
          className="absolute inset-x-0 top-full z-30 h-[100dvh] bg-[#14171a]/45"
        />
      ) : null}
      {openGroup ? (
        <div
          id={panelId}
          onMouseEnter={() => clearTimeout(timer.current)}
          onClick={event => {
            // A link to the current page does not change the pathname.
            if ((event.target as HTMLElement).closest('a')) setOpenId(null);
          }}
          className="custom-section absolute inset-x-0 top-full z-40 border-t border-[#e6e8e2] bg-white py-10 shadow-[0_24px_48px_rgba(13,26,13,0.12)]"
        >
          <div className="custom-container">
            <MegaMenuPanel group={openGroup} benefits={benefits} />
          </div>
        </div>
      ) : null}
    </div>
  );
}
